import { useEffect, useState } from 'react';
import throttle from 'lodash.throttle';

import useMatchMedia from 'utils/hooks/useMatchMedia';
import handleTrailingSlash from '../../../shared/base/utils/handleTrailingSlash';

export default function usePopup(pagesToAvoidPopup, isMergeVersion, deactivateInAllPages) {
  const [popupIsVisible, setPopupIsVisible] = useState(false);
  const [multiplePagesVisited, setMultiplePagesVisited] = useState(0);
  const isTabletAndDown = useMatchMedia('(max-width: 768px)');
  const closePopup = () => setPopupIsVisible(false);

  const shouldPopupBeAvoided = () => {
    const avoidedSlugs = pagesToAvoidPopup?.map(({ slug, __typename }) => {
      const finalSlug = __typename === 'ContentfulGlobalPost' ? `/blog${slug}` : slug;

      return handleTrailingSlash(finalSlug);
    });

    return avoidedSlugs?.includes(window.location.pathname);
  };

  const openPopup = () => {
    if (popupShown() || shouldPopupBeAvoided() || deactivateInAllPages) return;
    setPopupIsVisible(true);

    try {
      sessionStorage.setItem('popupShown', true);
    } catch (e) {
      console.error('sessionStorage quota exceeded.', e);
      sessionStorage.clear();
      sessionStorage.setItem('popupShown', true);
    }
    if (isMergeVersion) window.scrollTo(0, 0);
  };

  const popupShown = () => {
    return sessionStorage.getItem('popupShown');
  };

  const isBooking = () => {
    const url = window.location.pathname;
    // prettier-ignore
    /* eslint-disable-next-line no-useless-escape */
    const regex = new RegExp('^\/booking(-\w+)*$');
    return regex.test(url);
  };

  const handleOnScrollUp = throttle(() => {
    const halfPageVisited = sessionStorage.getItem('halfPageScrolled');
    if (!halfPageVisited) return;
    const scrolled = window.scrollY;
    const threshold = 300;
    if (scrolled <= threshold) openPopup();
  }, 500);

  const handleHalfPageScrolled = throttle(() => {
    const scrolled = window.scrollY;
    const halfPage = document.body.clientHeight / 2;
    if (scrolled >= halfPage)
      try {
        sessionStorage.setItem('halfPageScrolled', true);
      } catch (e) {
        console.error('sessionStorage quota exceeded.', e);
        sessionStorage.clear();
      }
  }, 500);

  const handleOnPopState = event => {
    if (event.state) openPopup();
  };

  // Checks if the user visited more than one page
  // window.history.length will output 2 Because the new tab, or window, has a default page, which also counts as history. In Chrome's case, it's the apps/most visited page.
  // If you enter the website from a link, and thus not opening the "new tab" page inbetween, it will output 1.
  // https://stackoverflow.com/questions/9564041/why-history-length-is-2-for-the-first-page/9564075
  useEffect(() => {
    if (isMergeVersion) {
      setTimeout(() => {
        openPopup();
      }, 1000);
      return;
    }
    if (document.referrer) setMultiplePagesVisited(window.history.length > 1);
    else setMultiplePagesVisited(window.history.length > 2);
  }, [isMergeVersion]);

  useEffect(() => {
    if (!multiplePagesVisited || popupShown() || isBooking()) return;
    if (isTabletAndDown) {
      const state = { name: 'popUpOnBrowserBack' };
      const title = document.title;
      const url = window.location.href;
      window.history.pushState(state, title, url);
      window.addEventListener('popstate', handleOnPopState);
      window.addEventListener('scroll', handleOnScrollUp);
      window.addEventListener('scroll', handleHalfPageScrolled);
    } else {
      document.documentElement.addEventListener('mouseleave', openPopup);
    }
    return () => {
      window.removeEventListener('popstate', handleOnPopState);
      window.removeEventListener('scroll', handleOnScrollUp);
      window.removeEventListener('scroll', handleHalfPageScrolled);
      document.documentElement.removeEventListener('mouseleave', openPopup);
    };
  }, [isTabletAndDown, multiplePagesVisited]);

  return {
    popupIsVisible,
    closePopup,
  };
}
